import { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Award, Clock, Download, FileText, Loader2, RotateCw, X } from "lucide-react";
import { subjectLabel } from "./subjectLabels";
import { errMsg } from "./paywall";

const md = (c) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{c}</ReactMarkdown>
);

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const MockTest = ({ api, authConfig, subject, chapter, onClose }) => {
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | paper | marking | report
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");
  const submittedRef = useRef(false);
  const genOnceRef = useRef(false);
  const answersRef = useRef({});
  answersRef.current = answers;

  useEffect(() => {
    if (genOnceRef.current) return;
    genOnceRef.current = true;
    generate();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (phase !== "paper") return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(t); submit(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [phase]);

  async function generate() {
    setPhase("loading"); setErr(""); setReport(null); setAnswers({});
    submittedRef.current = false;
    try {
      const r = await axios.post(`${api}/tutor/mocktest`, { subject, chapter: chapter || null }, authConfig);
      setTest(r.data.test);
      setSecondsLeft(r.data.test.duration_min * 60);
      setPhase("paper");
    } catch (e) { setErr(errMsg(e, "Could not set the paper.")); setPhase("error"); }
  }

  async function submit(auto = false) {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("marking"); setErr("");
    try {
      const arr = test.questions.map((_, i) => answersRef.current[i] || "");
      const r = await axios.post(`${api}/tutor/mocktest/${test.test_id}/submit`, { answers: arr }, authConfig);
      setReport(r.data);
      setPhase("report");
    } catch (e) {
      submittedRef.current = false;
      setErr(errMsg(e, "Marking failed — try submitting again."));
      setPhase("paper");
    }
  }

  async function downloadPdf() {
    setDownloading(true);
    try {
      const r = await axios.get(`${api}/tutor/mocktest/${test.test_id}/report.pdf`, { ...authConfig, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ShansoTutor-ReportCard.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { setErr("Could not download the report card."); }
    finally { setDownloading(false); }
  }

  const attempted = test ? Object.keys(answers).filter((k) => (answers[k] || "").trim()).length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-4" data-testid="mock-test-overlay">
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-5 sm:p-6 relative">
        <button onClick={onClose} data-testid="mock-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
        <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2"><FileText size={13}/> Mock test · 20 marks · board-style written answers</p>
        <h3 className="text-white font-bold text-lg mb-4">{subjectLabel(subject)} · {chapter || "Full Syllabus"}</h3>

        {err && <p className="text-red-300 text-sm mb-3" data-testid="mock-error">⚠ {err}</p>}

        {phase === "loading" && (
          <div className="py-16 text-center">
            <Loader2 className="animate-spin text-emerald-400 mx-auto mb-3" size={24}/>
            <p className="text-zinc-400 text-sm">Shanso Ma'am is setting your 20-mark paper (1, 2, 3 &amp; 5-mark questions)…</p>
          </div>
        )}

        {phase === "error" && (
          <button onClick={generate} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white hover:border-emerald-400"><RotateCw size={12}/> Try again</button>
        )}

        {phase === "marking" && (
          <div className="py-16 text-center" data-testid="mock-marking">
            <Loader2 className="animate-spin text-emerald-400 mx-auto mb-3" size={24}/>
            <p className="text-zinc-300 text-sm font-semibold">Shanso Ma'am is marking your paper like the board marking scheme…</p>
            <p className="text-zinc-500 text-xs mt-1">Step-wise marks, model answers and your report card — about a minute.</p>
          </div>
        )}

        {phase === "paper" && test && (
          <div data-testid="mock-paper">
            <div className={`sticky top-0 z-10 -mx-1 px-1 py-2 bg-[#0A0A0A] flex items-center justify-between mb-3 border-b border-zinc-800`}>
              <span className={`flex items-center gap-1.5 text-sm font-black px-3 py-1.5 rounded-full border ${secondsLeft <= 300 ? "border-red-400/50 text-red-300 bg-red-500/10" : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"}`} data-testid="mock-timer">
                <Clock size={13}/> {fmt(secondsLeft)}
              </span>
              <span className="text-zinc-500 text-xs">{attempted}/{test.questions.length} attempted · unanswered = 0 marks</span>
            </div>
            <div className="space-y-5 mb-5">
              {test.questions.map((q, i) => (
                <div key={i} data-testid={`mock-q${i}`}>
                  <div className="flex items-start gap-2 mb-1.5">
                    <span className="text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-1.5 py-0.5 rounded-full font-bold shrink-0 mt-0.5">{q.marks} mk</span>
                    <div className="text-sm text-zinc-100 prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${q.q}`)}</div>
                  </div>
                  <textarea value={answers[i] || ""} onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                    data-testid={`mock-answer-${i}`} rows={q.marks >= 3 ? 5 : 2} maxLength={2500}
                    placeholder="Write your answer step by step, like in the board exam…"
                    className="w-full bg-[#111] border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:border-emerald-400 outline-none resize-y"/>
                </div>
              ))}
            </div>
            <button onClick={() => submit(false)} data-testid="mock-submit-btn"
              className="w-full py-3 rounded-full text-sm font-bold text-black"
              style={{ background: "linear-gradient(135deg, #10B981, #34D399)" }}>
              Submit paper for marking ({attempted}/{test.questions.length} attempted)
            </button>
          </div>
        )}

        {phase === "report" && report && (
          <div data-testid="mock-report">
            <div className={`rounded-xl p-5 mb-5 text-center border ${report.pct >= 60 ? "bg-emerald-500/10 border-emerald-400/40" : "bg-amber-500/10 border-amber-400/30"}`}>
              <p className="text-4xl font-black text-white" data-testid="mock-score">{report.total}/{report.max_total}</p>
              <p className="text-zinc-300 text-sm mt-1">{report.pct}% · Grade <span className="font-black text-white">{report.grade}</span></p>
            </div>
            {report.remarks && (
              <div className="rounded-xl border border-zinc-800 bg-[#0F0F0F] p-4 mb-5">
                <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5"><Award size={12}/> Teacher's remarks</p>
                <p className="text-zinc-300 text-sm" data-testid="mock-remarks">{report.remarks}</p>
              </div>
            )}
            <div className="space-y-3 mb-5">
              {report.results.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3.5 ${r.awarded >= r.marks ? "border-emerald-500/30 bg-emerald-500/5" : r.awarded > 0 ? "border-amber-500/30 bg-amber-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid={`mock-result-q${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-zinc-100 prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${r.q}`)}</div>
                    <span className={`shrink-0 text-sm font-black ${r.awarded >= r.marks ? "text-emerald-300" : r.awarded > 0 ? "text-amber-300" : "text-red-300"}`}>{r.awarded}/{r.marks}</span>
                  </div>
                  {r.feedback && <p className="text-zinc-400 text-xs mt-2">{r.feedback}</p>}
                  {r.model_answer && (
                    <details className="mt-2">
                      <summary className="text-emerald-300 text-xs cursor-pointer">Model answer</summary>
                      <div className="text-zinc-300 text-xs mt-1.5 prose prose-invert prose-sm max-w-none">{md(r.model_answer)}</div>
                    </details>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadPdf} disabled={downloading} data-testid="mock-download-pdf-btn"
                className="px-4 py-2.5 rounded-full text-sm font-bold text-black flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #10B981, #34D399)" }}>
                {downloading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Report card PDF for parents
              </button>
              <button onClick={onClose} data-testid="mock-done-btn" className="px-4 py-2.5 rounded-full text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
